import { Link, useLocation } from 'react-router-dom';

type UserRole = 'submitter' | 'pastor' | 'minister_of_music' | 'musician' | 'media_team' | 'admin' | 'finance_team';

export interface Tab {
  icon: string;
  label: string;
  href: string;
}

interface TabBarProps {
  role: UserRole;
  className?: string;
}

export const ROLE_TABS: Record<UserRole, Tab[]> = {
  submitter: [
    { icon: '\uD83D\uDCCB', label: 'My Items', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2795', label: 'Submit', href: '/submissions/new' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
  pastor: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2705', label: 'Review', href: '/review' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
  minister_of_music: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83C\uDFB5', label: 'Music', href: '/songs' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
  musician: [
    { icon: '\uD83C\uDFB5', label: 'My Songs', href: '/my-assignments' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
    { icon: '\u2699\uFE0F', label: 'Settings', href: '/settings/routing' },
  ],
  media_team: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/media' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDCCB', label: 'Services', href: '/services' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
  admin: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\u2705', label: 'Review', href: '/review' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
  finance_team: [
    { icon: '\uD83D\uDCCA', label: 'Dashboard', href: '/dashboard' },
    { icon: '\uD83D\uDCC5', label: 'Calendar', href: '/calendar' },
    { icon: '\uD83D\uDCB0', label: 'Finance', href: '/reports/finance' },
    { icon: '\uD83D\uDD14', label: 'Alerts', href: '/notifications' },
  ],
};

export const ROLE_COLORS: Record<UserRole, string> = {
  submitter: 'text-primary',
  pastor: 'text-primary',
  minister_of_music: 'text-music',
  musician: 'text-music',
  media_team: 'text-primary',
  admin: 'text-primary',
  finance_team: 'text-primary',
};

export function TabBar({ role, className = '' }: TabBarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const tabs = ROLE_TABS[role];
  const activeColor = ROLE_COLORS[role];

  return (
    <nav
      className={`
        fixed bottom-0 left-0 right-0
        bg-[var(--color-bg)] border-t border-[var(--color-border)]
        md:hidden
        ${className}
      `.trim().replace(/\s+/g, ' ')}
    >
      <div className="max-w-xl mx-auto flex">
        {tabs.map((tab) => {
          const isActive = pathname === tab.href || (tab.href !== '/' && pathname.startsWith(tab.href));
          const textColor = isActive ? activeColor : 'text-text-muted';
          const fontWeight = isActive ? 'font-semibold' : 'font-normal';

          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={`
                flex-1 flex flex-col items-center justify-center
                py-2 px-1
                ${textColor} ${fontWeight}
                transition-colors duration-200
                hover:opacity-80
              `.trim().replace(/\s+/g, ' ')}
            >
              <span className="text-h2 mb-0.5">{tab.icon}</span>
              <span className="text-micro leading-tight">{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
